/**
 * Flow Queuing System — every READ the enroll-queue context performs, plus
 * the query-shaped derived state built straight off those reads. Extracted
 * verbatim from `EnrollQueueContext.tsx` (F9 god-context cut 2 of 4).
 *
 * The extraction boundary is deliberate: it ends exactly where `addMutation`
 * begins. The mutations block (`addMutation`/`patchMutation`/`removeMutation`/
 * `unstickMutation`/`sendMutation`) stays in the context file, reserved for
 * the F1/DBOS worktree — nothing here reads or reorders it. The cache keys
 * both halves share live in `queue-query-keys.ts` so neither module has to
 * import the other.
 *
 * Pure move: every query config, comment, memo, and callback below is
 * byte-identical to its previous home. The only additions are the hook
 * wrapper and its return object.
 */
import { useCallback, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import {
  QueueResponseSchema,
  SendersResponseSchema,
  InputOptionsResponseSchema,
  type QueueResponse,
  type InputFieldOptions,
  type SenderOption,
  type SendersResponse,
} from '@shared/flow/enrollment';
import { PlayCatalogResponseSchema, type PlayCatalogResponse } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import { useFlowRequests, useInvalidateRequestsOnStream } from './requests-hooks';
import { useInvalidatePhoneRequestsOnStream } from '../trellus/phone-requests-hooks';
import { usePhoneFoundToasts } from '../trellus/phoneFoundToast';
import { useGongFlowsSuggested } from './gong-flow-queries';
import { FLOW_QUEUE_PLAYS_KEY, INPUT_OPTIONS_KEY, QUEUE_KEY, SENDERS_KEY } from './queue-query-keys';
import type { DefaultSenderState, EnrollQueueContextValue } from './enroll-queue-types';

/**
 * Stable empty result for `inputOptionsFor` — a fresh `[]` per call would give
 * consumers a new array identity on every render, re-running the editor's
 * default-selection effect (which WRITES) on every unrelated SSE re-render.
 */
const NO_INPUT_OPTIONS: InputFieldOptions[] = [];
/** Stable empty result for `gongFlowsSuggested` while unloaded — same
 * referential-stability rationale as `NO_INPUT_OPTIONS` above. */
const NO_GONG_FLOWS_SUGGESTED: GongFlowSummary[] = [];

/**
 * The context value's read-only half, plus `queueData` — the raw
 * `QueueResponse` the provider's ghost-entry sweep re-validates against the
 * schema before it deletes any local step-override draft.
 */
export type EnrollQueueQueries = Pick<
  EnrollQueueContextValue,
  | 'groups'
  | 'plays'
  | 'activePlayTypes'
  | 'gongFlowsSuggested'
  | 'sendersFor'
  | 'defaultSenderFor'
  | 'inputOptionsFor'
  | 'totalCount'
  | 'isLoading'
  | 'playsResolved'
> & { queueData: QueueResponse | undefined };

export function useEnrollQueueQueries(): EnrollQueueQueries {
  const { apiFetch } = useApi();

  const queueQuery = useQuery<QueueResponse>({
    queryKey: QUEUE_KEY,
    queryFn: () => fetchJson(apiFetch, QueueResponseSchema, '/api/flow/queue'),
    // Cache-indefinitely + invalidate-on-mutation (CLAUDE.md caching rule) —
    // every add/assign/remove mutation below invalidates QUEUE_KEY.
    staleTime: Infinity,
  });

  // Keep the UNFILTERED Requests feed mounted at shell level so its cache entry
  // exists whether or not the Actions tab is open: the optimistic prepend
  // (`prependOptimisticRequest`, fired when an instant action is run) and the
  // stream-driven refetch below both write to it. `ActionsIndex` runs its OWN
  // `useFlowRequests(q)` for its (possibly search-filtered) list; React Query
  // shares the cache entry with this one when `q === ''`.
  useFlowRequests('');
  // Live-updates the Requests feed's cache on `execution_status_changed` —
  // called once here (Provider is mounted once at shell level) so the feed
  // stays fresh regardless of which tab/route is active.
  useInvalidateRequestsOnStream();
  useInvalidatePhoneRequestsOnStream();
  // The auto-dismissing "Found <number> for <name>" toast after a single-contact find; merges close-together finds into one updating toast (survives the row's button unmounting).
  usePhoneFoundToasts();

  const playsQuery = useQuery<PlayCatalogResponse>({
    queryKey: FLOW_QUEUE_PLAYS_KEY,
    queryFn: () => fetchJson(apiFetch, PlayCatalogResponseSchema, '/api/flow/queue/plays'),
    // Indefinite cache; invalidated by the Play Manager admin's own mutations
    // (PlaysPage) via FLOW_QUEUE_PLAYS_KEY. Admin changes made by ANOTHER
    // user's browser still need a reload — acceptable v0.
    staleTime: Infinity,
  });

  // U12 — gong group wiring: this rep's suggested-3 Gong flows, part of the
  // SAME "plays fan-out" as `playsQuery` above but its own query (a
  // different endpoint, `GET /api/flow/gong/flows/suggested`, not the
  // general play catalog). Invalidated by `useSetGongFlowPreference`
  // (gong-flow-queries.ts) whenever a pin/hide mutation succeeds, from
  // wherever that mutation is fired (the browse overlay) — React Query's
  // cache is shared app-wide regardless of which component declared the
  // query.
  const gongFlowsSuggestedQuery = useGongFlowsSuggested();
  const gongFlowsSuggested = gongFlowsSuggestedQuery.data ?? NO_GONG_FLOWS_SUGGESTED;

  // One senders query PER DISTINCT LIVE PLAY TYPE (today: one). Senders are
  // play-type config (play-senders.ts registry) — event_invite gets the
  // Playhouse list; future types (Gong, Sendoso) have their own semantics.
  //
  // Codex retry finding (plan `2026-08-22-001-gong-flow-play-1312-inpr-
  // fixes-plan.md` U8 follow-up): this MUST be the union of `plays`' and
  // `active_play_types`' play_type_ids, not `plays` alone. A cold-start
  // install has zero `gong_flows` mirror rows, so `plays` carries no
  // gong_flow entry even though the TYPE is live (`active_play_types`
  // marks that — see R5e above). An assign still succeeds in that state
  // (PlayPicker's `gongCatalogEntry` reads `active_play_types`), but
  // scanning only `plays` here left `sendersFor(gongTypeId)` permanently
  // `[]` — no sender query was ever issued for that type, so the item could
  // never clear "Needs a sender" (itemBlockReason.ts / no `no_sender`
  // server-side default exists either).
  const playTypeIds = useMemo(
    () => [...new Set([
      ...(playsQuery.data?.plays ?? []).map(p => p.play_type_id),
      ...(playsQuery.data?.active_play_types ?? []).map(t => t.play_type_id),
    ])],
    [playsQuery.data],
  );
  const senderQueries = useQueries({
    queries: playTypeIds.map(playTypeId => ({
      queryKey: [...SENDERS_KEY, playTypeId] as const,
      // D12 point 3 — the WHOLE response is cached, not just `senders`: the
      // same payload now carries the role-derived `default_sender` the
      // play-level Sender field displays, and splitting them into two
      // queries would double the fan-out for one endpoint.
      queryFn: (): Promise<SendersResponse> =>
        fetchJson(apiFetch, SendersResponseSchema, `/api/flow/queue/senders?play_type_id=${playTypeId}`),
      // Static per-type code-owned list — indefinite cache.
      staleTime: Infinity,
      // This is a speculative prefetch across every active play type (see
      // the U8 union above), not a fetch for what the user is currently
      // looking at — a type that's active in the DB but hasn't shipped its
      // play-senders.ts registration yet (or a local-only fixture type) must
      // still throw loudly (no silent fallback), but shouldn't blast the
      // global error toast for a background query nobody asked for.
      meta: { suppressErrorToast: true },
    })),
  });

  // U8 — one input-options query PER ASSIGNED ITEM. Same `useQueries` shape as
  // the senders fan-out above, keyed one level down (item, not play type),
  // because these options are per-CONTACT. Unassigned items are skipped: with
  // no play type there is no field that could declare an `optionsSource`, and
  // the endpoint would answer `{ fields: [] }` anyway. Every other play type
  // gets one cheap empty response per item — the same per-item fan-out
  // `WarningBadges` already does for every rendered queue row.
  const inputOptionItemIds = useMemo(
    () => (queueQuery.data?.groups ?? [])
      .flatMap(group => group.items)
      .filter(item => item.play_type_id !== null)
      .map(item => item.id),
    [queueQuery.data],
  );
  const inputOptionQueries = useQueries({
    queries: inputOptionItemIds.map(itemId => ({
      queryKey: [...INPUT_OPTIONS_KEY, itemId] as const,
      queryFn: async (): Promise<InputFieldOptions[]> => {
        const parsed = await fetchJson(apiFetch, InputOptionsResponseSchema, `/api/flow/queue/items/${itemId}/input-options`);
        return parsed.fields;
      },
      // Fix 5b (MAJOR bd finding): this list mirrors `salesforce_warm_intro`,
      // which `SfdcObjectSyncWorkflow` refreshes HOURLY (adds/deletes real
      // rows, not just edits) — `staleTime: Infinity` alone (the senders
      // list / play catalog's posture, appropriate for admin-owned config
      // that invalidates explicitly on its own mutations) would keep
      // showing a connector that vanished (or hide one that just appeared)
      // for the rest of the session.
      //
      // A numeric TTL (e.g. 15 min) is what the original finding asked for,
      // but the repo's `terra/no-ttl-literals` lint rule (.oxlintrc.json,
      // `error`, no override for this directory) hard-bans any numeric
      // `staleTime` — only `Infinity` or `0` pass, and `0` would make EVERY
      // new observer mount (e.g. a second editor surface subscribing to the
      // SAME item) treat cached data as immediately stale and re-fetch.
      //
      // `refetchOnWindowFocus: 'always'` is the compliant fix: unlike plain
      // `true` (gated behind staleness — a no-op under `Infinity`),
      // `'always'` bypasses staleTime entirely and refetches on every
      // window refocus. CLAUDE.md's caching rule prefers invalidate-on-
      // mutation FIRST — see the explicit `refreshInputOptionsIfStale` /
      // `selectInputOption` 400-invalidation below, which cover the two
      // real trigger points — and this refocus refetch is the last-resort
      // complement for the case NEITHER fires (the picker never opens, the
      // PATCH never fails, yet the row set drifted), without a banned
      // numeric literal.
      staleTime: Infinity,
      refetchOnWindowFocus: 'always',
    })),
  });

  // Memoized against the query's own data reference (not a bare `?? []`
  // fallback literal, which allocates a new array every render and defeats
  // the useMemo below it — react-hooks/exhaustive-deps catches this).
  const queueData = queueQuery.data;
  const playsData = playsQuery.data;
  const groups = useMemo(() => queueData?.groups ?? [], [queueData]);
  const plays = useMemo(() => playsData?.plays ?? [], [playsData]);
  // R5e — see EnrollQueueContextValue.activePlayTypes' doc comment.
  const activePlayTypes = useMemo(() => playsData?.active_play_types ?? [], [playsData]);
  // Map play_type_id -> its loaded sender options (parallel arrays from
  // useQueries; unloaded/errored entries simply have no map entry yet).
  const sendersByPlayType = useMemo(() => {
    const map = new Map<number, SenderOption[]>();
    playTypeIds.forEach((playTypeId, i) => {
      const data = senderQueries[i]?.data;
      if (data) map.set(playTypeId, data.senders);
    });
    return map;
  }, [playTypeIds, senderQueries]);
  const sendersFor = useCallback(
    (playTypeId: number | null) => (playTypeId === null ? [] : sendersByPlayType.get(playTypeId) ?? []),
    [sendersByPlayType],
  );
  // D12 point 3 — the same parallel-arrays walk for the role-derived default
  // that rides along in each senders response. A separate map (not a
  // second field on the one above) so `sendersFor`'s array identity stays
  // stable per query result — the editor's default-selection effect depends
  // on it.
  const defaultSenderByPlayType = useMemo(() => {
    const map = new Map<number, string | null>();
    playTypeIds.forEach((playTypeId, i) => {
      const data = senderQueries[i]?.data;
      // `default_sender` is optional on the wire (Codex D12 follow-up: old
      // callers/mocks may omit the key entirely) — normalize `undefined` to
      // `null` here so this map's VALUES stay `string | null`. D12 Codex fix
      // #5: an ABSENT map entry now carries real meaning ("the query for this
      // play type hasn't resolved, or failed"), distinct from a present-null
      // entry ("resolved: this play/rep genuinely has no default"), so
      // `.get()`'s undefined must never be flattened into `null` by the
      // reader below.
      //
      // D12 Codex ROUND-2 fix #7 — the gate is KEY PRESENCE, not truthiness of
      // the response. Round 1 made the schema field `.nullable().optional()`
      // (correct) but kept setting the entry for ANY successful response, so
      // an old-shape/degraded payload that omits `default_sender` entirely
      // produced `resolved: true` with `sender: null` — identical to a NEW
      // response that genuinely resolved to no default. That defeated the
      // whole point of `resolved`: "Reset to base copy" would still write
      // `sender: null` over a working sender when talking to an old-shape
      // response, which is exactly the bug `resolved` was built to prevent.
      //
      // `'default_sender' in data` is a real discriminator here, not a
      // formality: Zod omits an absent optional key from its output object
      // (verified against the installed zod for this schema — absent ⇒ `in`
      // is false; explicit `null` ⇒ `in` is true), so a resolved-to-null
      // default still lands an entry and only a genuinely missing key does
      // not. `validateResponse` logs-and-returns a shape-mismatched payload
      // AS-IS (it never throws), so this is also the guard for a truncated
      // response arriving as a truthy `data`.
      //
      // D12 Codex ROUND-3 fix #5 — the object check is mandatory, not
      // belt-and-braces. `in` throws `TypeError: Cannot use 'in' operator` for
      // a non-object right-hand side, and `validateResponse` deliberately
      // passes a malformed payload through UNCHANGED rather than erroring — so
      // a degraded response body of `true` or `"error"` (a proxy error page, a
      // truncated JSON primitive) arrives here as a truthy `data` that is not
      // an object, and round 2's bare `data && 'default_sender' in data` threw
      // inside this `useMemo`, crashing the whole queue render tree. The TS
      // type says `SendersResponse`, which at a trust boundary is an assertion
      // rather than a guarantee (team conventions, "Schema-First Data
      // Boundaries"). A non-object payload now simply lands no entry, i.e.
      // reads as UNRESOLVED — the same conservative answer key-absence gets.
      if (typeof data === 'object' && data !== null && 'default_sender' in data) {
        map.set(playTypeId, data.default_sender ?? null);
      }
    });
    return map;
  }, [playTypeIds, senderQueries]);
  const defaultSenderFor = useCallback(
    (playTypeId: number | null): DefaultSenderState => {
      // A null play type never issues a senders query at all, so there is no
      // "resolved" answer to give — reported as UNRESOLVED (not as a genuine
      // null default) because the only consumer that acts on `resolved`
      // ("Reset to base copy") must fall back to the item's own sender rather
      // than write a null over it.
      if (playTypeId === null) return { resolved: false, sender: null };
      if (!defaultSenderByPlayType.has(playTypeId)) return { resolved: false, sender: null };
      return { resolved: true, sender: defaultSenderByPlayType.get(playTypeId) ?? null };
    },
    [defaultSenderByPlayType],
  );
  // Same parallel-arrays walk as `sendersByPlayType`, keyed by item id (U8).
  const inputOptionsByItem = useMemo(() => {
    const map = new Map<number, InputFieldOptions[]>();
    inputOptionItemIds.forEach((itemId, i) => {
      const data = inputOptionQueries[i]?.data;
      if (data) map.set(itemId, data);
    });
    return map;
  }, [inputOptionItemIds, inputOptionQueries]);
  const inputOptionsFor = useCallback(
    // The map's own array identity is stable per query result, so an editor's
    // default-selection effect doesn't re-run on unrelated re-renders. The
    // empty fallback is the module-level constant for the same reason.
    (itemId: number) => inputOptionsByItem.get(itemId) ?? NO_INPUT_OPTIONS,
    [inputOptionsByItem],
  );
  const totalCount = useMemo(() => groups.reduce((n, g) => n + g.items.length, 0), [groups]);
  // U-B: see the `playsResolved` doc comment on EnrollQueueContextValue —
  // deliberately `!== 'pending'`, not `isSuccess`, so a FAILED catalog fetch
  // still unlocks consumers instead of dead-locking them.
  const playsResolved = playsQuery.status !== 'pending';

  return {
    queueData,
    groups,
    plays,
    activePlayTypes,
    gongFlowsSuggested,
    sendersFor,
    defaultSenderFor,
    inputOptionsFor,
    totalCount,
    isLoading: queueQuery.isLoading,
    playsResolved,
  };
}
